import { RecaptchaModule } from 'ng-recaptcha';
import { RecaptchaFormsModule } from 'ng-recaptcha';
import { MarkdownModule, MarkedOptions } from 'ngx-markdown';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, SecurityContext } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CdkTableModule } from '@angular/cdk/table';

import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatLegacyTableModule as MatTableModule } from '@angular/material/legacy-table';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FlexLayoutModule, LayoutGapStyleBuilder } from '@angular/flex-layout';
import { CustomLayoutGapStyleBuilder } from './shared/LayoutGapBuilder';

import { AppRoutingModule } from './app-routing.module';

import { CacheInterceptor } from './http-interceptors/cache-interceptor';
import { AuthInterceptor } from './http-interceptors/auth-interceptor';

import { PdfViewerModule } from 'ng2-pdf-viewer';

import { markedOptionsFactory } from './factories/marked-options-factory';

import { LogService } from './services/log.service';

import { SessionService } from './services/session.service';
import {
  UserService,
  MaintenanceGuard,
  OnlyLoggedInUsersGuard,
  OnlyHasSecurityQuestionUsersGuard,
  OnlyHasParticipantIdUsersGuard,
  OnlyHasNoteIdRelationshipTypeUsersGuard
} from './services/user.service';
import {
  QuickPayService,
  QP_OnlyLoggedInUsersGuard,
  QP_OnlyHasParticipantIdUsersGuard,
} from './services/quickpay.service'
import { MessageService } from './services/message.service';
import { HttpErrorHandler } from './services/http-error-handler.service';
import { ErrorHandler } from './services/error-handler.service';
import { UtilityService } from './services/utility.service';
import { ReferenceService } from './services/reference.service';
import { ApplicationService } from './services/application.service';
import { CompanyService } from './services/company.service';
import { NavigationService } from './services/navigation.service';
import { RegistrationService } from './services/registration.service';
import { ContactService } from './services/contact.service';
import { NoteService } from './services/note.service';
import { ContractService } from './services/contract.service';
import { PaymentService } from './services/payment.service';

import { AppComponent } from './app.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { PageFooterComponent } from './components/page-footer/page-footer.component';
import { HomeComponent } from './components/home/home.component';
import { LoanDetailsComponent } from './components/loan-details/loan-details.component';
import { NoteDetailComponent } from './components/note-detail/note-detail.component';
import { NoteRepayScheduleListComponent } from './components/note-repay-schedule-list/note-repay-schedule-list.component';
// tslint:disable-next-line:max-line-length
import { NoteDisbursementScheduleListComponent } from './components/note-disbursement-schedule-list/note-disbursement-schedule-list.component';
import { EsignDetailsComponent } from './components/esign-details/esign-details.component';
import { EsignNoteComponent } from './components/esign-note/esign-note.component';
import { PaymentNowComponent } from './components/payment-now/payment-now.component';
import { PaymentNoteListComponent } from './components/payment-note-list/payment-note-list.component';
import { PaymentFormComponent } from './components/payment-form/payment-form.component';
import { PaymentHistoryComponent } from './components/payment-history/payment-history.component';
import { PaymentAutomaticComponent } from './components/payment-automatic/payment-automatic.component';
import { PdfDocumentViewerDialogComponent } from './components/pdf-document-viewer-dialog/pdf-document-viewer-dialog.component';
import { PaymentAmountComponent } from './components/payment-amount/payment-amount.component';
import { PaymentConfirmationDialogComponent } from './components/payment-confirmation-dialog/payment-confirmation-dialog.component';
import { PaymentHistoryListComponent } from './components/payment-history-list/payment-history-list.component';
// tslint:disable-next-line:max-line-length
import { NotePaymentHistoryDetailsListComponent } from './components/note-payment-history-details-list/note-payment-history-details-list.component';
import { NoteBalanceAdjustmentsListComponent } from './components/note-balance-adjustments-list/note-balance-adjustments-list.component';
import { NoteTotalAmountPaidComponent } from './components/note-total-amount-paid/note-total-amount-paid.component';
import { TableRecordCountComponent } from './components/table-record-count/table-record-count.component';
import { ExcelExportComponent } from './components/excel-export/excel-export.component';
import { PaymentAutomaticNoteListComponent } from './components/payment-automatic-note-list/payment-automatic-note-list.component';
import { PaymentAutomaticFormComponent } from './components/payment-automatic-form/payment-automatic-form.component';
import { FaqComponent } from './components/faq/faq.component';
import { LoginComponent } from './components/login/login.component';
// tslint:disable-next-line:max-line-length
import { PaymentAutomaticConfirmationDialogComponent } from './components/payment-automatic-confirmation-dialog/payment-automatic-confirmation-dialog.component';
import { fromEventPattern } from 'rxjs';
import { ContactUsComponent } from './components/contact-us/contact-us.component';
import { ContactInformationComponent } from './components/contact-information/contact-information.component';
import { UserRegistrationComponent } from './components/user-registration/user-registration.component';
import { QuickPayComponent } from './components/quick-pay-login/quick-pay-login.component';
import { QuickPayPaymentFormComponent } from './components/quick-pay-payment-form/quick-pay-payment-form.component';
import { QuickPay_PaymentComponent } from './components/quick-pay-payment/quick-pay-payment.component';
import { QuickPayPaymentNoteListComponent } from './components/quick-pay-payment-note-list/quick-pay-payment-note-list.component';
import { QuickPayPaymentAmountComponent } from './components/quick-pay-payment-amount/quick-pay-payment-amount.component';
import { QuickPayPaymentConfirmationDialogComponent } from './components/quick-pay-payment-confirmation-dialog/quick-pay-payment-confirmation-dialog.component';
import { MultifactorVerifyComponent } from './components/multifactor-verify/multifactor-verify.component';
import { ForgotUserNameComponent } from './components/forgot-user-name/forgot-user-name.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { LoginOneTimeComponent } from './components/login-one-time/login-one-time.component';
import { UserSecurityQuestionComponent } from './components/user-security-question/user-security-question.component';
import { MobileDisplayDialogComponent } from './components/mobile-display-dialog/mobile-display-dialog.component';
import { MaintenanceComponent } from './components/maintenance/maintenance.component';
import { DefaultUrlSerializer, UrlSerializer, UrlTree } from '@angular/router';

export class CaseInsensitiveUrlSerializer extends DefaultUrlSerializer {
  parse(url: string): UrlTree {
    return super.parse(url.toLowerCase());
  }

  serialize(tree: UrlTree): string {
    return super.serialize(tree).toLowerCase();
  }
}
@NgModule({
  declarations: [
    AppComponent,
    PageHeaderComponent,
    PageFooterComponent,
    HomeComponent,
    LoanDetailsComponent,
    NoteDetailComponent,
    NoteRepayScheduleListComponent,
    NoteDisbursementScheduleListComponent,
    EsignDetailsComponent,
    EsignNoteComponent,
    PaymentNowComponent,
    PaymentNoteListComponent,
    PaymentFormComponent,
    PaymentHistoryComponent,
    PaymentAutomaticComponent,
    PdfDocumentViewerDialogComponent,
    PaymentAmountComponent,
    PaymentConfirmationDialogComponent,
    PaymentHistoryListComponent,
    NotePaymentHistoryDetailsListComponent,
    NoteBalanceAdjustmentsListComponent,
    NoteTotalAmountPaidComponent,
    TableRecordCountComponent,
    ExcelExportComponent,
    PaymentAutomaticNoteListComponent,
    PaymentAutomaticFormComponent,
    PaymentAutomaticConfirmationDialogComponent,
    ContactInformationComponent,
    FaqComponent,
    LoginComponent,
    QuickPayComponent,
    QuickPay_PaymentComponent,
    QuickPayPaymentFormComponent,
    QuickPayPaymentNoteListComponent,
    QuickPayPaymentAmountComponent,
    QuickPayPaymentConfirmationDialogComponent,
    ContactUsComponent,
    UserRegistrationComponent,
    MultifactorVerifyComponent,
    ForgotUserNameComponent,
    ForgotPasswordComponent,
    LoginOneTimeComponent,
    UserSecurityQuestionComponent,
    MobileDisplayDialogComponent,
    MaintenanceComponent
  ],
  imports: [
    MarkdownModule.forRoot({
      loader: HttpClient,
      markedOptions: {
        provide: MarkedOptions,
        useFactory: markedOptionsFactory
      }
    }),
    BrowserModule,
    BrowserAnimationsModule,
    RecaptchaModule,
    RecaptchaFormsModule,
    CdkTableModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDialogModule,
    MatDividerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatSelectModule,
    MatSidenavModule,
    MatSlideToggleModule,
    MatSliderModule,
    MatSnackBarModule,
    MatSortModule,
    MatStepperModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    FormsModule,
    ReactiveFormsModule,
    FlexLayoutModule,
    AppRoutingModule,
    HttpClientModule,
    PdfViewerModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: LayoutGapStyleBuilder, useClass: CustomLayoutGapStyleBuilder },
    { provide: UrlSerializer, useClass: CaseInsensitiveUrlSerializer },
    UserService,
    QuickPayService,
    MaintenanceGuard,
    OnlyLoggedInUsersGuard,
    OnlyHasSecurityQuestionUsersGuard,
    OnlyHasParticipantIdUsersGuard,
    OnlyHasNoteIdRelationshipTypeUsersGuard,
    LogService,
    QP_OnlyLoggedInUsersGuard,
    QP_OnlyHasParticipantIdUsersGuard,
    SessionService,
    MessageService,
    HttpErrorHandler,
    ErrorHandler,
    UtilityService,
    ReferenceService,
    ApplicationService,
    CompanyService,
    NavigationService,
    RegistrationService,
    ContactService,
    NoteService,
    ContractService,
    PaymentService
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    PdfDocumentViewerDialogComponent,
    PaymentConfirmationDialogComponent,
    PaymentAutomaticConfirmationDialogComponent,
    MobileDisplayDialogComponent
  ]
})

export class AppModule {}
